import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
import type { CalendarLaneRefusal } from "./calendarClient";
import {
  CONSULTING_TAX_POLICIES,
  CONSULTING_TAX_POLICY_REGIONS,
  CONSULTING_TAX_POLICY_WORDS,
  US_STATE_NAMES,
  type ConsultingTaxPolicy,
} from "../../../../../../backend/convex/lib/consultingTaxPolicies.js";
// CNS1 — the World's consulting time through the local server's grant door
// (the CNS1 lane over the TM13 custody path). The browser names a World and
// plain fields; the Actor, the bearer and every authority check live
// server-side and at the World. Each failure arrives as the lane's typed
// refusal envelope and is surfaced as `{ ok: false, kind, reason }` — never
// thrown, never retried here, so a refusal is something the face can show.
//
// One rule this module exists to keep: a booking carries the deduction and
// terms revision the person accepted at the quote. The caller cannot book
// without a quote in hand, and the World refuses a stale one rather than
// holding more minutes than were agreed.

interface ConsultingDoorRefusal extends CalendarLaneRefusal {
  readonly definitiveRefusal?: boolean;
  readonly code?: string;
}
async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<{ readonly body: Record<string, unknown> } | ConsultingDoorRefusal> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) {
    const reason =
      typeof body?.["reason"] === "string"
        ? body["reason"]
        : "The consulting lane refused this request.";
    const kind = typeof body?.["kind"] === "string" ? body["kind"] : "transport";
    return {
      ok: false,
      kind,
      reason,
      ...(body?.["definitiveRefusal"] === true ? { definitiveRefusal: true } : {}),
      ...(typeof body?.["code"] === "string" ? { code: body["code"] } : {}),
    };
  }
  return { body };
}

const asNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);
const rows = (value: unknown): ReadonlyArray<Record<string, unknown>> =>
  Array.isArray(value)
    ? (value.filter((row) => typeof row === "object" && row !== null) as Record<string, unknown>[])
    : [];

// ── What the faces read ──────────────────────────────────────────────────

/** A published sales-tax policy and the US regions it sells to: the World's own list. */
export { CONSULTING_TAX_POLICY_REGIONS, CONSULTING_TAX_POLICY_WORDS, US_STATE_NAMES };
export type { ConsultingTaxPolicy };
const isTaxPolicy = (value: unknown): value is ConsultingTaxPolicy =>
  (CONSULTING_TAX_POLICIES as readonly unknown[]).includes(value);

export interface ConsultingTerms {
  readonly salesTaxPolicy?: ConsultingTaxPolicy;
  readonly termsId: string;
  readonly revision: number;
  readonly currency: "USD";
  readonly rateCentsPerHour: number;
  readonly purchaseMinimumMinutes: number;
  readonly purchaseIncrementMinutes: number;
  readonly minimumBillableMinutes: number;
  readonly billingIncrementMinutes: number;
  readonly allowedMeetingMinutes: readonly number[];
  readonly lateCancelNoticeMinutes: number;
  readonly lateCancelChargeMinutes: number;
  readonly lateRescheduleNoticeMinutes?: number;
  readonly lateRescheduleChargeMinutes?: number;
}
export function parseConsultingTerms(value: unknown): ConsultingTerms | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (
    (row["salesTaxPolicy"] !== undefined && !isTaxPolicy(row["salesTaxPolicy"])) ||
    typeof row["termsId"] !== "string" ||
    !row["termsId"] ||
    row["currency"] !== "USD" ||
    ![
      "revision",
      "rateCentsPerHour",
      "purchaseMinimumMinutes",
      "purchaseIncrementMinutes",
      "minimumBillableMinutes",
      "billingIncrementMinutes",
    ].every((k) => typeof row[k] === "number" && Number.isSafeInteger(row[k]) && row[k] > 0) ||
    !["lateCancelNoticeMinutes", "lateCancelChargeMinutes"].every(
      (k) => typeof row[k] === "number" && Number.isSafeInteger(row[k]) && row[k] >= 0,
    ) ||
    !["lateRescheduleNoticeMinutes", "lateRescheduleChargeMinutes"].every(
      (k) =>
        row[k] === undefined ||
        (typeof row[k] === "number" && Number.isSafeInteger(row[k]) && row[k] >= 0),
    ) ||
    !Array.isArray(row["allowedMeetingMinutes"]) ||
    row["allowedMeetingMinutes"].length === 0 ||
    !row["allowedMeetingMinutes"].every(
      (k) => typeof k === "number" && Number.isSafeInteger(k) && k > 0,
    )
  )
    return null;
  return row as unknown as ConsultingTerms;
}

export interface ConsultantSummary {
  readonly profileId: string;
  readonly displayName: string;
  readonly headline: string;
  readonly expertise: ReadonlyArray<string>;
  readonly availableMinutes: number;
  readonly rateCentsPerHour: number | null;
  readonly purchaseMinimumMinutes: number | null;
  readonly purchaseIncrementMinutes: number | null;
  readonly status: string;
  readonly retainedAccount?: {
    readonly accountId: string;
    readonly debtMinutes: number;
    readonly disputedMinutes: number;
  };
  readonly meetings: ReadonlyArray<{
    readonly bookingPageId: string;
    readonly title: string;
    readonly durationMinutes: number;
  }>;
}

export type ConsultantsOutcome =
  | { readonly ok: true; readonly consultants: ReadonlyArray<ConsultantSummary> }
  | CalendarLaneRefusal;

export async function listConsultants(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultantsOutcome> {
  const outcome = await call("/api/neucleos/consulting/consultants", { worldKey }, fetchImpl);
  if ("ok" in outcome) return outcome;
  const consultants = rows(outcome.body["consultants"]).flatMap((row) => {
    const profileId = asString(row["profileId"]),
      displayName = asString(row["displayName"]);
    if (profileId === null || displayName === null) return [];
    const terms =
      typeof row["terms"] === "object" && row["terms"] !== null
        ? (row["terms"] as Record<string, unknown>)
        : null;
    return [
      {
        profileId,
        displayName,
        headline: asString(row["headline"]) ?? "",
        expertise: Array.isArray(row["expertise"])
          ? (row["expertise"] as unknown[]).filter(
              (item): item is string => typeof item === "string",
            )
          : [],
        availableMinutes: asNumber(row["availableMinutes"]) ?? 0,
        rateCentsPerHour: terms ? asNumber(terms["rateCentsPerHour"]) : null,
        purchaseMinimumMinutes: terms ? asNumber(terms["purchaseMinimumMinutes"]) : null,
        purchaseIncrementMinutes: terms ? asNumber(terms["purchaseIncrementMinutes"]) : null,
        status: "active",
        meetings: rows(row["meetings"]).flatMap((meeting) => {
          const bookingPageId = asString(meeting["bookingPageId"]),
            title = asString(meeting["title"]);
          const durationMinutes = asNumber(meeting["durationMinutes"]);
          return bookingPageId !== null && title !== null && durationMinutes !== null
            ? [{ bookingPageId, title, durationMinutes }]
            : [];
        }),
      },
    ];
  });
  return { ok: true, consultants };
}

export interface ConsultingBalance {
  readonly purchasedMinutes: number;
  readonly refundedMinutes: number;
  readonly adjustedMinutes: number;
  readonly reservedMinutes: number;
  readonly consumedMinutes: number;
  readonly availableMinutes: number;
  readonly consultant: string;
  readonly consultantStatus: string;
}

export interface ConsultingAllowanceView {
  readonly memberActorId: string;
  readonly status: string;
  readonly capMinutes: number | null;
  readonly canApproveWork: boolean;
  readonly reservedMinutes: number;
  readonly consumedMinutes: number;
  readonly remainingMinutes: number | null;
  readonly revision: number;
}

export interface ConsultingWorkView {
  readonly workItemId: string;
  readonly title: string;
  readonly status: string;
  readonly revision: number;
  readonly estimateMinutes: number | null;
  readonly capMinutes: number | null;
  readonly proposedCapMinutes: number | null;
}

export interface ConsultingAccountView {
  readonly view: "founder" | "member";
  readonly account: ConsultingBalance | null;
  readonly allowance: ConsultingAllowanceView | null;
  readonly allowances: ReadonlyArray<ConsultingAllowanceView>;
  readonly work: ReadonlyArray<ConsultingWorkView>;
  readonly reservations: ReadonlyArray<{
    readonly reservationId: string;
    readonly unit: string;
    readonly reservedMinutes: number;
    readonly billedMinutes: number;
  }>;
  readonly history: ReadonlyArray<{
    readonly seq: number;
    readonly kind: string;
    readonly minutes: number;
    readonly reason: string;
    readonly at: number;
  }>;
}

export type ConsultingAccountOutcome =
  | ({ readonly ok: true } & ConsultingAccountView)
  | CalendarLaneRefusal;

function parsedAllowance(row: Record<string, unknown>): ConsultingAllowanceView | null {
  const memberActorId = asString(row["memberActorId"]);
  if (memberActorId === null) return null;
  return {
    memberActorId,
    status: asString(row["status"]) ?? "active",
    capMinutes: asNumber(row["capMinutes"]),
    canApproveWork: row["canApproveWork"] === true,
    reservedMinutes: asNumber(row["reservedMinutes"]) ?? 0,
    consumedMinutes: asNumber(row["consumedMinutes"]) ?? 0,
    remainingMinutes: asNumber(row["remainingMinutes"]),
    revision: asNumber(row["revision"]) ?? 0,
  };
}

export async function readConsultingAccount(
  worldKey: string,
  profileId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingAccountOutcome> {
  const outcome = await call(
    "/api/neucleos/consulting/account",
    { worldKey, profileId },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const body = outcome.body;
  const balance =
    typeof body["account"] === "object" && body["account"] !== null
      ? (body["account"] as Record<string, unknown>)
      : null;
  const account: ConsultingBalance | null =
    balance === null
      ? null
      : {
          purchasedMinutes: asNumber(balance["purchasedMinutes"]) ?? 0,
          refundedMinutes: asNumber(balance["refundedMinutes"]) ?? 0,
          adjustedMinutes: asNumber(balance["adjustedMinutes"]) ?? 0,
          reservedMinutes: asNumber(balance["reservedMinutes"]) ?? 0,
          consumedMinutes: asNumber(balance["consumedMinutes"]) ?? 0,
          availableMinutes: asNumber(balance["availableMinutes"]) ?? 0,
          consultant: asString(balance["consultant"]) ?? "the consultant",
          consultantStatus: asString(balance["consultantStatus"]) ?? "active",
        };
  return {
    ok: true,
    view: body["view"] === "founder" ? "founder" : "member",
    account,
    allowance:
      typeof body["allowance"] === "object" && body["allowance"] !== null
        ? parsedAllowance(body["allowance"] as Record<string, unknown>)
        : null,
    allowances: rows(body["allowances"]).flatMap((row) => {
      const parsed = parsedAllowance(row);
      return parsed ? [parsed] : [];
    }),
    work: rows(body["work"]).flatMap((row) => {
      const workItemId = asString(row["workItemId"]),
        title = asString(row["title"]);
      if (workItemId === null || title === null) return [];
      return [
        {
          workItemId,
          title,
          status: asString(row["status"]) ?? "requested",
          revision: asNumber(row["revision"]) ?? 0,
          estimateMinutes: asNumber(row["estimateMinutes"]),
          capMinutes: asNumber(row["capMinutes"]),
          proposedCapMinutes: asNumber(row["proposedCapMinutes"]),
        },
      ];
    }),
    reservations: rows(body["reservations"]).flatMap((row) => {
      const reservationId = asString(row["reservationId"]);
      if (reservationId === null) return [];
      return [
        {
          reservationId,
          unit: asString(row["unit"]) ?? "appointment",
          reservedMinutes: asNumber(row["reservedMinutes"]) ?? 0,
          billedMinutes: asNumber(row["billedMinutes"]) ?? 0,
        },
      ];
    }),
    history: rows(body["history"]).flatMap((row) => {
      const seq = asNumber(row["seq"]),
        minutes = asNumber(row["minutes"]),
        at = asNumber(row["at"]);
      const kind = asString(row["kind"]);
      if (seq === null || minutes === null || kind === null || at === null) return [];
      return [{ seq, kind, minutes, reason: asString(row["reason"]) ?? "", at }];
    }),
  };
}

export interface ConsultingDeskUnit {
  readonly reservationId: string;
  readonly unit: string;
  readonly world: string;
  readonly title: string;
  readonly startsAt: number | null;
  readonly reservedMinutes: number;
  readonly actualMinutes: number;
  readonly billedMinutes: number;
  readonly awaitingPosting: boolean;
  readonly workItemId: string | null;
  readonly workRevision: number | null;
  readonly proposedCapMinutes: number | null;
}

export type ConsultantDeskOutcome =
  | {
      readonly ok: true;
      readonly displayName: string;
      readonly status: string;
      readonly units: ReadonlyArray<ConsultingDeskUnit>;
      readonly customers: ReadonlyArray<{
        readonly world: string;
        readonly availableMinutes: number;
      }>;
      readonly workAwaitingEstimate: ReadonlyArray<{
        readonly workItemId: string;
        readonly title: string;
        readonly revision: number;
      }>;
    }
  | CalendarLaneRefusal;

export async function readConsultantDesk(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultantDeskOutcome> {
  const outcome = await call("/api/neucleos/consulting/desk", { worldKey }, fetchImpl);
  if ("ok" in outcome) return outcome;
  const body = outcome.body;
  const profile =
    typeof body["profile"] === "object" && body["profile"] !== null
      ? (body["profile"] as Record<string, unknown>)
      : {};
  return {
    ok: true,
    displayName: asString(profile["displayName"]) ?? "",
    status: asString(profile["status"]) ?? "active",
    units: rows(body["units"]).flatMap((row) => {
      const reservationId = asString(row["reservationId"]);
      if (reservationId === null) return [];
      return [
        {
          reservationId,
          unit: asString(row["unit"]) ?? "appointment",
          world: asString(row["world"]) ?? "World",
          title: asString(row["title"]) ?? "Appointment",
          startsAt: asNumber(row["startsAt"]),
          reservedMinutes: asNumber(row["reservedMinutes"]) ?? 0,
          actualMinutes: asNumber(row["actualMinutes"]) ?? 0,
          billedMinutes: asNumber(row["billedMinutes"]) ?? 0,
          awaitingPosting: row["awaitingPosting"] === true,
          workItemId: asString(row["workItemId"]),
          workRevision: asNumber(row["workRevision"]),
          proposedCapMinutes: asNumber(row["proposedCapMinutes"]),
        },
      ];
    }),
    customers: rows(body["customers"]).map((row) => ({
      world: asString(row["world"]) ?? "World",
      availableMinutes: asNumber(row["availableMinutes"]) ?? 0,
    })),
    workAwaitingEstimate: rows(body["workAwaitingEstimate"]).flatMap((row) => {
      const workItemId = asString(row["workItemId"]),
        title = asString(row["title"]);
      if (workItemId === null || title === null) return [];
      return [{ workItemId, title, revision: asNumber(row["revision"]) ?? 0 }];
    }),
  };
}

// ── The quote a person accepts, and the booking that carries it ──────────

export interface ConsultingQuote {
  readonly consultant: string;
  readonly meetingMinutes: number;
  readonly anticipatedDeductionMinutes: number;
  readonly acceptedDeductionMinutes: number;
  readonly acceptedTermsRevision: number;
  readonly worldAvailableMinutes: number;
  readonly allowanceRemainingMinutes: number | null;
  readonly authorized: boolean;
  readonly canBook: boolean;
}

export type ConsultingQuoteOutcome =
  | ({ readonly ok: true } & ConsultingQuote)
  | CalendarLaneRefusal;

export async function quoteConsultingBooking(
  worldKey: string,
  bookingPageId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingQuoteOutcome> {
  const outcome = await call(
    "/api/neucleos/consulting/quote",
    { worldKey, bookingPageId },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const body = outcome.body;
  const accepted = asNumber(body["acceptedDeductionMinutes"]);
  const revision = asNumber(body["acceptedTermsRevision"]);
  if (accepted === null || revision === null) {
    return {
      ok: false,
      kind: "transport",
      reason: "The quote did not say what booking it authorizes.",
    };
  }
  return {
    ok: true,
    consultant: asString(body["consultant"]) ?? "the consultant",
    meetingMinutes: asNumber(body["meetingMinutes"]) ?? 0,
    anticipatedDeductionMinutes: asNumber(body["anticipatedDeductionMinutes"]) ?? accepted,
    acceptedDeductionMinutes: accepted,
    acceptedTermsRevision: revision,
    worldAvailableMinutes: asNumber(body["worldAvailableMinutes"]) ?? 0,
    allowanceRemainingMinutes: asNumber(body["allowanceRemainingMinutes"]),
    authorized: body["authorized"] === true,
    canBook: body["canBook"] === true,
  };
}

export type ConsultingBookOutcome =
  | {
      readonly ok: true;
      readonly bookingId: string;
      readonly reservationId: string | null;
      readonly reservedMinutes: number;
      readonly replay: boolean;
    }
  | CalendarLaneRefusal;

/**
 * Books the time the person accepted. The quote is required, not optional:
 * its deduction and terms revision travel with the request, and the World
 * refuses (CONSULTING_QUOTE_STALE) rather than hold more than was agreed.
 */
export async function bookConsulting(
  worldKey: string,
  input: {
    readonly bookingPageId: string;
    readonly startsAt: number;
    readonly requestKey: string;
    readonly accepted: Pick<ConsultingQuote, "acceptedDeductionMinutes" | "acceptedTermsRevision">;
    readonly timeZone?: string;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingBookOutcome> {
  const outcome = await call(
    "/api/neucleos/consulting/book",
    {
      worldKey,
      bookingPageId: input.bookingPageId,
      startsAt: input.startsAt,
      requestKey: input.requestKey,
      acceptedDeductionMinutes: input.accepted.acceptedDeductionMinutes,
      acceptedTermsRevision: input.accepted.acceptedTermsRevision,
      ...(input.timeZone === undefined ? {} : { timeZone: input.timeZone }),
    },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const bookingId = asString(outcome.body["bookingId"]);
  if (bookingId === null)
    return { ok: false, kind: "transport", reason: "The booking did not name itself." };
  return {
    ok: true,
    bookingId,
    reservationId: asString(outcome.body["reservationId"]),
    reservedMinutes: asNumber(outcome.body["reservedMinutes"]) ?? 0,
    replay: outcome.body["replay"] === true,
  };
}

// ── The founder's authority, and the consultant's record of work ─────────

export type ConsultingAllowanceOutcome =
  | {
      readonly ok: true;
      readonly memberActorId: string;
      readonly capMinutes: number | null;
      readonly remainingMinutes: number | null;
    }
  | CalendarLaneRefusal;

/** capMinutes null is "no personal cap"; it creates no time and earmarks none. */
export async function setConsultingAllowance(
  worldKey: string,
  input: {
    readonly profileId: string;
    readonly memberActorId: string;
    readonly capMinutes: number | null;
    readonly canApproveWork?: boolean;
    readonly status?: "active" | "revoked";
  },
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingAllowanceOutcome> {
  const outcome = await call(
    "/api/neucleos/consulting/set-allowance",
    {
      worldKey,
      profileId: input.profileId,
      memberActorId: input.memberActorId,
      capMinutes: input.capMinutes,
      ...(input.canApproveWork === undefined ? {} : { canApproveWork: input.canApproveWork }),
      ...(input.status === undefined ? {} : { status: input.status }),
    },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const memberActorId = asString(outcome.body["memberActorId"]);
  if (memberActorId === null)
    return { ok: false, kind: "transport", reason: "The allowance did not name its member." };
  return {
    ok: true,
    memberActorId,
    capMinutes: asNumber(outcome.body["capMinutes"]),
    remainingMinutes: asNumber(outcome.body["remainingMinutes"]),
  };
}

export type ConsultingPostOutcome =
  | {
      readonly ok: true;
      readonly entryId: string;
      readonly actualMinutes: number;
      readonly billedMinutes: number;
      readonly unitRemainingMinutes: number;
      readonly replay: boolean;
    }
  | CalendarLaneRefusal;

export async function postConsultingTime(
  worldKey: string,
  input: {
    readonly reservationId: string;
    readonly actualMinutes: number;
    readonly description: string;
    readonly requestKey: string;
    readonly privateNote?: string;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingPostOutcome> {
  const outcome = await call(
    "/api/neucleos/consulting/post-time",
    {
      worldKey,
      reservationId: input.reservationId,
      actualMinutes: input.actualMinutes,
      description: input.description,
      requestKey: input.requestKey,
      ...(input.privateNote === undefined ? {} : { privateNote: input.privateNote }),
    },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const entryId = asString(outcome.body["entryId"]);
  if (entryId === null)
    return { ok: false, kind: "transport", reason: "The time entry did not name itself." };
  return {
    ok: true,
    entryId,
    actualMinutes: asNumber(outcome.body["actualMinutes"]) ?? input.actualMinutes,
    billedMinutes: asNumber(outcome.body["billedMinutes"]) ?? 0,
    unitRemainingMinutes: asNumber(outcome.body["unitRemainingMinutes"]) ?? 0,
    replay: outcome.body["replay"] === true,
  };
}

export type ConsultingCloseOutcome =
  | { readonly ok: true; readonly releasedMinutes: number; readonly billedMinutes: number }
  | CalendarLaneRefusal;

/** Closing releases what was held and never billed. Time is never deducted because an end time passed. */
export async function closeConsultingUnit(
  worldKey: string,
  input: {
    readonly reservationId: string;
    readonly outcome: "completed" | "no-show" | "not-needed";
  },
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingCloseOutcome> {
  const answered = await call(
    "/api/neucleos/consulting/close-unit",
    { worldKey, reservationId: input.reservationId, outcome: input.outcome },
    fetchImpl,
  );
  if ("ok" in answered) return answered;
  return {
    ok: true,
    releasedMinutes: asNumber(answered.body["releasedMinutes"]) ?? 0,
    billedMinutes: asNumber(answered.body["billedMinutes"]) ?? 0,
  };
}

export interface ConsultingPurchaseView {
  readonly taxAssessment?: {
    readonly policy: ConsultingTaxPolicy;
    readonly serviceCountry: "US";
    /** A two-letter US region the policy sells to (CONSULTING_TAX_POLICY_REGIONS). */
    readonly serviceRegion: string;
    readonly taxCents: 0;
    readonly acceptedServiceScope: true;
    readonly classification: "professional-advice-custom-business-applications";
  };
  readonly purchaseKey: string;
  readonly minutes: number;
  readonly amountCents: number;
  readonly status: string;
  readonly checkoutUrl: string | null;
  readonly sellerWorldKey: string | null;
  readonly sellerWorldName: string | null;
  readonly terms: ConsultingTerms | null;
  readonly stripeAccountId: string | null;
  readonly offerName: string | null;
  readonly livemode: boolean | null;
  /** Times a seller gave back held time for a refund Stripe never confirmed, newest last (at most twenty). */
  readonly refundReleases?: readonly ConsultingRefundRelease[];
  /** How many such releases were ever given; more than are listed when it exceeds the list. */
  readonly refundReleaseCount?: number;
}
export interface ConsultingRefundRelease {
  readonly operationId: string;
  readonly reason: string;
  readonly at: number;
  readonly byActorName: string | null;
  readonly releasedMinutes: number;
  readonly lateRefund: {
    readonly refundId: string;
    readonly at: number;
    readonly takenBackMinutes: number;
  } | null;
}
export interface ConsultingStoredPurchase extends ConsultingPurchaseView {
  readonly requestKey: string;
  readonly offerRevisionId: string | null;
}
export type ConsultingPurchaseOutcome =
  | ({ readonly ok: true } & ConsultingPurchaseView)
  | ConsultingDoorRefusal;
export function isStripeCheckoutUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.hostname === "checkout.stripe.com" &&
      !url.username &&
      !url.password &&
      !url.port
    );
  } catch {
    return false;
  }
}
function parsedPurchase(value: unknown): ConsultingPurchaseView | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  if (row["taxAssessment"] !== undefined) {
    const tax = row["taxAssessment"] as Record<string, unknown> | null;
    if (
      !tax ||
      typeof tax !== "object" ||
      !isTaxPolicy(tax["policy"]) ||
      tax["serviceCountry"] !== "US" ||
      !CONSULTING_TAX_POLICY_REGIONS[tax["policy"]].includes(String(tax["serviceRegion"])) ||
      tax["taxCents"] !== 0 ||
      tax["acceptedServiceScope"] !== true ||
      tax["classification"] !== "professional-advice-custom-business-applications"
    )
      return null;
  }
  if (
    typeof row["purchaseKey"] !== "string" ||
    ![
      "reserved",
      "open",
      "processing",
      "failed",
      "paid",
      "refunded",
      "disputed",
      "expired",
    ].includes(String(row["status"])) ||
    !["minutes", "amountCents"].every(
      (k) => typeof row[k] === "number" && Number.isSafeInteger(row[k]) && row[k] > 0,
    ) ||
    !["checkoutUrl", "sellerWorldKey", "sellerWorldName", "stripeAccountId", "offerName"].every(
      (k) => row[k] === null || typeof row[k] === "string",
    ) ||
    !(row["livemode"] === null || typeof row["livemode"] === "boolean") ||
    !(row["terms"] === null || parseConsultingTerms(row["terms"]) !== null) ||
    (typeof row["checkoutUrl"] === "string" && !isStripeCheckoutUrl(row["checkoutUrl"]))
  )
    return null;
  return row as unknown as ConsultingPurchaseView;
}
const purchaseUnknown = (): CalendarLaneRefusal => ({
  ok: false,
  kind: "transport",
  reason:
    "The purchase response is incomplete or belongs to another World. Its outcome is unknown; keep this attempt and refresh purchases.",
});

/** Opens one hosted Checkout. Nothing is funded here: the person pays, and the processor's own record credits the minutes. */
export async function startConsultingPurchase(
  worldKey: string,
  input: {
    readonly serviceCountry?: string;
    readonly serviceRegion?: string;
    readonly acceptedServiceScope?: boolean;
    readonly profileId: string;
    readonly minutes?: number;
    readonly offerRevisionId?: string;
    readonly requestKey: string;
    readonly acceptedTermsId: string;
  },
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingPurchaseOutcome> {
  const outcome = await call(
    "/api/neucleos/consulting/start-purchase",
    { worldKey, ...input },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const view = parsedPurchase(outcome.body);
  return view && outcome.body["worldKey"] === worldKey ? { ...view, ok: true } : purchaseUnknown();
}

export async function readConsultingPurchases(
  worldKey: string,
  profileId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<
  | { readonly ok: true; readonly purchases: readonly ConsultingStoredPurchase[] }
  | CalendarLaneRefusal
> {
  const outcome = await call(
    "/api/neucleos/consulting/purchases",
    { worldKey, profileId },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const body = outcome.body;
  if (
    body["worldKey"] !== worldKey ||
    body["profileId"] !== profileId ||
    !Array.isArray(body["purchases"])
  )
    return purchaseUnknown();
  const purchases = body["purchases"].map((value: unknown) => {
    const view = parsedPurchase(value);
    const row = value as Record<string, unknown> | null;
    return view &&
      typeof row?.["requestKey"] === "string" &&
      (row["offerRevisionId"] === null || typeof row["offerRevisionId"] === "string")
      ? {
          ...view,
          requestKey: row["requestKey"],
          offerRevisionId: row["offerRevisionId"] as string | null,
        }
      : null;
  });
  return purchases.includes(null)
    ? purchaseUnknown()
    : { ok: true, purchases: purchases as ConsultingStoredPurchase[] };
}
async function changePurchase(
  worldKey: string,
  purchaseKey: string,
  operation: "reconcile" | "cancel",
  fetchImpl: typeof fetch,
): Promise<ConsultingPurchaseOutcome> {
  const outcome = await call(
    `/api/neucleos/consulting/${operation}-purchase`,
    { worldKey, purchaseKey },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const view = parsedPurchase(outcome.body);
  return view && view.purchaseKey === purchaseKey && outcome.body["worldKey"] === worldKey
    ? { ...view, ok: true }
    : purchaseUnknown();
}
export const reconcileConsultingPurchase = (
  worldKey: string,
  purchaseKey: string,
  fetchImpl: typeof fetch = fetch,
) => changePurchase(worldKey, purchaseKey, "reconcile", fetchImpl);
export const cancelConsultingPurchase = (
  worldKey: string,
  purchaseKey: string,
  fetchImpl: typeof fetch = fetch,
) => changePurchase(worldKey, purchaseKey, "cancel", fetchImpl);

export async function readConsultingRelationships(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
  cursor: string | null = null,
): Promise<
  | {
      readonly ok: true;
      readonly consultants: readonly ConsultantSummary[];
      readonly hasMore: boolean;
      readonly nextCursor: string | null;
    }
  | CalendarLaneRefusal
> {
  const result = await call(
    "/api/neucleos/consulting/relationships",
    { worldKey, ...(cursor === null ? {} : { cursor }) },
    fetchImpl,
  );
  if ("ok" in result) return result;
  const body = result.body;
  if (
    body["worldKey"] !== worldKey ||
    !Array.isArray(body["relationships"]) ||
    typeof body["hasMore"] !== "boolean" ||
    !(body["nextCursor"] === null || typeof body["nextCursor"] === "string") ||
    (body["hasMore"] && !body["nextCursor"])
  )
    return {
      ok: false,
      kind: "transport",
      reason: "Retained consulting relationships are incomplete or belong to another World.",
    };
  const consultants = body["relationships"].map((value: unknown) => {
    if (!value || typeof value !== "object") return null;
    const row = value as Record<string, unknown>;
    const terms =
      row["terms"] !== null && typeof row["terms"] === "object"
        ? (row["terms"] as Record<string, unknown>)
        : null;
    if (
      !["profileId", "displayName", "accountId"].every((k) => typeof row[k] === "string") ||
      !["active", "paused", "withdrawn", "unavailable"].includes(String(row["status"])) ||
      !["availableMinutes", "debtMinutes", "disputedMinutes"].every(
        (k) => typeof row[k] === "number" && Number.isFinite(row[k]),
      )
    )
      return null;
    return {
      profileId: row["profileId"] as string,
      displayName: row["displayName"] as string,
      status: row["status"] as string,
      headline: "",
      expertise: [],
      availableMinutes: row["availableMinutes"] as number,
      rateCentsPerHour: terms ? asNumber(terms["rateCentsPerHour"]) : null,
      purchaseMinimumMinutes: terms ? asNumber(terms["purchaseMinimumMinutes"]) : null,
      purchaseIncrementMinutes: terms ? asNumber(terms["purchaseIncrementMinutes"]) : null,
      meetings: [],
      retainedAccount: {
        accountId: row["accountId"] as string,
        debtMinutes: row["debtMinutes"] as number,
        disputedMinutes: row["disputedMinutes"] as number,
      },
    } satisfies ConsultantSummary;
  });
  return consultants.includes(null)
    ? {
        ok: false,
        kind: "transport",
        reason:
          "A retained consulting relationship did not supply its stored identity and balance.",
      }
    : {
        ok: true,
        consultants: consultants as ConsultantSummary[],
        hasMore: body["hasMore"],
        nextCursor: body["nextCursor"] as string | null,
      };
}

// ── The times a member may take, before the quote is carried into a booking ──

export interface ConsultingSlotDay {
  readonly dayStart: number;
  readonly weekday: number;
  readonly slots: ReadonlyArray<{ readonly startsAt: number; readonly endsAt: number }>;
}

export type ConsultingSlotsOutcome =
  | {
      readonly ok: true;
      readonly title: string;
      readonly durationMinutes: number;
      readonly hostTimeZone: string;
      readonly days: ReadonlyArray<ConsultingSlotDay>;
    }
  | CalendarLaneRefusal;

/** The offered times on a consulting page, as the World computes them for this member (TM24's slot read). */
export async function listConsultingSlots(
  worldKey: string,
  input: { readonly bookingPageId: string; readonly fromDay: number; readonly days: number },
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultingSlotsOutcome> {
  const outcome = await call(
    "/api/neucleos/booking/member-slots",
    { worldKey, ...input },
    fetchImpl,
  );
  if ("ok" in outcome) return outcome;
  const body = outcome.body;
  return {
    ok: true,
    title: asString(body["title"]) ?? "",
    durationMinutes: asNumber(body["durationMinutes"]) ?? 0,
    hostTimeZone: asString(body["hostTimeZone"]) ?? "UTC",
    days: rows(body["days"]).flatMap((day) => {
      const dayStart = asNumber(day["dayStart"]);
      if (dayStart === null) return [];
      return [
        {
          dayStart,
          weekday: asNumber(day["weekday"]) ?? new Date(dayStart).getUTCDay(),
          slots: rows(day["slots"]).flatMap((slot) => {
            const startsAt = asNumber(slot["startsAt"]),
              endsAt = asNumber(slot["endsAt"]);
            return startsAt !== null && endsAt !== null ? [{ startsAt, endsAt }] : [];
          }),
        },
      ];
    }),
  };
}

// Setup uses the same protected transport as the other consulting operations.
export { call as callConsultingDoor };
export interface ConsultantSetupProfile {
  readonly profileId: string;
  readonly displayName: string;
  readonly headline: string;
  readonly expertise: readonly string[];
  readonly timeZone: string;
  readonly status: "active" | "paused" | "withdrawn";
}
export type ConsultantSetupOutcome =
  | {
      readonly ok: true;
      readonly profile: ConsultantSetupProfile | null;
      readonly terms: ConsultingTerms | null;
    }
  | CalendarLaneRefusal;
export async function readConsultantSetup(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConsultantSetupOutcome> {
  const result = await call("/api/neucleos/consulting/setup", { worldKey }, fetchImpl);
  if ("ok" in result) return result;
  // The read leaves out a profile or terms not yet set up (Can 27); absent is none.
  const profile = result.body.profile ?? null;
  const terms = result.body.terms ?? null;
  const unknown: CalendarLaneRefusal = {
    ok: false,
    kind: "transport",
    reason: "Provider setup is incomplete or belongs to another World. Refresh its saved records.",
  };
  if (result.body.worldKey !== worldKey) return unknown;
  if (profile === null) return terms === null ? { ok: true, profile: null, terms: null } : unknown;
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) return unknown;
  const row = profile as Record<string, unknown>;
  if (
    !["profileId", "displayName", "headline", "timeZone"].every(
      (k) => typeof row[k] === "string",
    ) ||
    !["active", "paused", "withdrawn"].includes(String(row.status)) ||
    !Array.isArray(row.expertise) ||
    row.expertise.length > 24 ||
    !row.expertise.every((k) => typeof k === "string")
  )
    return unknown;
  const parsedTerms = terms === null ? null : parseConsultingTerms(terms);
  if (terms !== null && parsedTerms === null) return unknown;
  return { ok: true, profile: row as unknown as ConsultantSetupProfile, terms: parsedTerms };
}

/** PR #1625: who can see and buy a consultant's packages (absent on the World's side is public). */
export type ConsultingAudience = "public" | "invited";
/** One audience's catalog card, exactly as the World wrote it: its lines (the last one names the digest) and digest. */
export interface ConsultingCatalogCard {
  readonly lines: ReadonlyArray<readonly [label: string, text: string]>;
  readonly digest: string;
}
/** The catalog card read for an approval: loading, failed (said why), or the World's card for each audience. */
export type ConsultingCatalogRead =
  | { readonly status: "reading" }
  | { readonly status: "failed"; readonly reason: string }
  | {
      readonly status: "loaded";
      readonly audience: ConsultingAudience;
      readonly cards: Readonly<Record<ConsultingAudience, ConsultingCatalogCard>>;
    };

const CATALOG_UNREADABLE =
  "The World did not answer this consultant's catalog card, so what a buyer agrees to cannot be shown.";
function catalogCard(value: unknown): ConsultingCatalogCard | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const lines = row["lines"];
  if (typeof row["digest"] !== "string" || !/^[0-9a-f]{64}$/.test(row["digest"])) return null;
  if (!Array.isArray(lines) || lines.length === 0) return null;
  const parsed: Array<readonly [string, string]> = [];
  for (const line of lines) {
    if (!Array.isArray(line) || line.length !== 2 || typeof line[0] !== "string" || typeof line[1] !== "string")
      return null;
    parsed.push([line[0], line[1]]);
  }
  return { lines: parsed, digest: row["digest"] };
}

/**
 * PR #1625: the consultant's catalog card from the same setup read (neucleos_read_consultant_setup's `catalog`), for
 * the approval card of an audience change. Every line is the World's; nothing here composes a line.
 */
export async function readConsultingCatalog(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Exclude<ConsultingCatalogRead, { status: "reading" }>> {
  const result = await call("/api/neucleos/consulting/setup", { worldKey }, fetchImpl);
  if ("ok" in result) return { status: "failed", reason: result.reason };
  const catalog = result.body["catalog"];
  if (result.body["worldKey"] !== worldKey || !catalog || typeof catalog !== "object" || Array.isArray(catalog))
    return { status: "failed", reason: CATALOG_UNREADABLE };
  const row = catalog as Record<string, unknown>;
  // The World answers no card when the catalog is past what one card can show; its sentence says so.
  if (typeof row["unavailable"] === "string") return { status: "failed", reason: row["unavailable"] };
  const audience = row["audience"];
  const cards = { public: catalogCard(row["public"]), invited: catalogCard(row["invited"]) };
  if ((audience !== "public" && audience !== "invited") || cards.public === null || cards.invited === null)
    return { status: "failed", reason: CATALOG_UNREADABLE };
  return { status: "loaded", audience, cards: { public: cards.public, invited: cards.invited } };
}
